import { Link } from "@/components/navigation-controls/link";
import { FilterByTextAppliedFilterView } from "@/domain/view/components/datatable/datatable-filters/filter-by-text-applied-filter-view";
import { FilterByTextConfigurationView } from "@/domain/view/components/datatable/datatable-filters/filter-by-text-configuration-view";
import { FilterByTextDisplayView } from "@/domain/view/components/datatable/datatable-filters/filter-by-text-display-view";
import { filterByTextWithUnknownPayload } from "@/domain/view/components/datatable/datatable-filters/functions/filter-by-text";
import { DatatableCellTitle } from "@/domain/view/components/datatable/datatable-template-cells/datatable-cell-title";
import { DatatableColumnHeaderCell } from "@/domain/view/components/datatable/datatable-template-cells/datatable-column-header-cell";
import { DatatableColumn, isRawRow } from "@/domain/view/components/datatable/datatable-types";
import { Workspace } from "@/domain/workspace/models/workspace-model";
import { cn } from "@/lib/cn";

import { IndicatorListItem } from "../../../models/indicator-model";

export const COLUMN_ID_INDICATOR_TITLE = "indicator_title";
export const COLUMN_ORDER_VALUE_INDICATOR_TITLE = 103;
export const COLUMN_ID_INDICATOR_DISPLAY_VALUE = "Titel";

interface IndicatorTitleColumnProps {
  workspace: Workspace;
  columnId?: string;
  orderValue?: number;
  displayValue?: string;
}

export const IndicatorTitleColumn = ({
  workspace,
  columnId = COLUMN_ID_INDICATOR_TITLE,
  orderValue = COLUMN_ORDER_VALUE_INDICATOR_TITLE,
  displayValue = COLUMN_ID_INDICATOR_DISPLAY_VALUE,
}: IndicatorTitleColumnProps): DatatableColumn<IndicatorListItem> => {
  return {
    columnId: columnId,
    getDefaultVisibilityState: () => true,
    getDisplayValue: () => displayValue,
    getOrderValue: () => orderValue, // prefix with 100, 200, 300 to assign to sections
    enableSectioning: false,
    getColumnDef: () => {
      return {
        id: columnId,
        accessorKey: columnId, // key needed to enable sorting / filtering
        enableHiding: false,
        enableSorting: true,
        enableColumnFilter: true,
        sortingFn: (rowA, rowB) => {
          if (!isRawRow(rowA.original)) {
            return 0;
          }

          if (!isRawRow(rowB.original)) {
            return 0;
          }

          return rowA.original.title.localeCompare(rowB.original.title);
        },
        filterFn: (row, _columnId, value, _addMeta) => {
          if (!isRawRow(row.original)) {
            // never filter section rows
            return true;
          }

          return filterByTextWithUnknownPayload(value, row.original.title);
        },
        header: ({ column }) => (
          <DatatableColumnHeaderCell column={column} className={`pl-2 pr-1 text-xs font-normal`}>
            Indikator
          </DatatableColumnHeaderCell>
        ),
        cell: ({ row }) => {
          if (!isRawRow(row.original)) {
            return null;
          }

          const indicator = row.original;
          return (
            <DatatableCellTitle row={row} className="box-border min-w-[10rem]">
              <Link
                to={`/${workspace.workspaceUrl}/indicators/${indicator.id}`}
                className={cn(["max-w-full overflow-hidden text-ellipsis whitespace-nowrap pl-4 font-medium"])}
              >
                {indicator.title !== "" ? indicator.title : <span className="text-muted-foreground">Kein Titel</span>}
              </Link>
            </DatatableCellTitle>
          );
        },
      };
    },
    renderDisplayView: () => <FilterByTextDisplayView displayValue={displayValue} />,
    getFilterConfigurationViewType: () => "modal",
    renderFilterConfigurationView: (onFinished: () => void) => (
      <FilterByTextConfigurationView column={{ id: columnId }} onFinished={onFinished} />
    ),
    renderAppliedFilterView: () => (
      <FilterByTextAppliedFilterView
        column={{ id: columnId }}
        displayValue={displayValue}
        inputPlaceholder="Filter Sie nach Titel"
      />
    ),
  };
};
